import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { NJButton, NJHeading } from '@engie-group/fluid-design-system-react';
import { RightSideModal } from './RightSideModalFixed';

export interface ProductionLineFormData {
    identifier: string;
    name: string;
    electricityContract: string;
    coolingType: string;
    minFlowHalfLine: number;
    minFlowFullLine: number;
    maxFlow: number;
    priority: number;
    postProductionShutdown: boolean;
    evaporatorPumpGroups: string[];
    chillers: string[];
    condenserPumpGroups: string[];
}

interface ProductionLineEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: Partial<ProductionLineFormData>;
    onSubmit: (data: ProductionLineFormData) => void;
}

const ELECTRICITY_CONTRACT_OPTIONS = [
    'Contrat elec Auber',
    'Contrat elec Canada',
    'Contrat elec DLTM',
    'Contrat elec Etoile',
    'Contrat elec Halles',
    'Contrat elec Opera',
];

const COOLING_TYPE_OPTIONS = [
    'Refroidissement par air',
    'Refroidissement par eau',
    'Refroidissement mixte',
];

const FIELD_STYLE: React.CSSProperties = {
    width: '100%', minHeight: 40, padding: '10px 12px',
    border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
    borderRadius: 4, fontSize: 14, boxSizing: 'border-box',
};

const LABEL_STYLE: React.CSSProperties = { display: 'block', fontSize: 14, marginBottom: 6 };

export function ProductionLineEditModal({
    isOpen,
    onClose,
    initialData,
    onSubmit,
}: ProductionLineEditModalProps) {
    const defaultValues = {
        identifier: '',
        name: '',
        electricityContract: 'Energy_Forum',
        coolingType: 'Refroidissement par eau',
        minFlowHalfLine: 50,
        minFlowFullLine: 100,
        maxFlow: 500,
        priority: 1,
        postProductionShutdown: false,
        evaporatorPumpGroups: [],
        chillers: [],
        condenserPumpGroups: [],
    };

    const { register, handleSubmit, reset } = useForm<ProductionLineFormData>({
        defaultValues: initialData ?? defaultValues,
    });

    useEffect(() => {
        if (isOpen && initialData) {
            reset(initialData);
        }
    }, [isOpen, initialData, reset]);

    const handleFormSubmit = (data: ProductionLineFormData) => {
        onSubmit(data);
    };

    return (
        <RightSideModal
            isOpen={isOpen}
            onClose={onClose}
            title={initialData ? 'Éditer ligne de production' : 'Créer ligne de production'}
            metadata={
                initialData
                    ? `Modifié par PROTOTYPER le ${new Date().toLocaleDateString('fr-FR')}`
                    : `Créé par PROTOTYPER le ${new Date().toLocaleDateString('fr-FR')}`
            }
            footer={
                <>
                    <NJButton label="Annuler" emphasis="subtle" onClick={onClose} />
                    <NJButton label="Valider" emphasis="bold" onClick={handleSubmit(handleFormSubmit)} />
                </>
            }
        >
            <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                * Champs obligatoires
            </div>

            {/* Section: Paramètres généraux */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Paramètres généraux
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Identifiant</label>
                    <input
                        {...register('identifier')}
                        disabled={!!initialData}
                        placeholder="LIGNE-001"
                        style={{
                            ...FIELD_STYLE,
                            ...(initialData ? { backgroundColor: 'var(--nj-semantic-color-surface-disabled)' } : {}),
                        }}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Nom *</label>
                    <input
                        {...register('name', { required: true })}
                        placeholder="Ligne 1"
                        style={FIELD_STYLE}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Contrat d'électricité auxiliaire *</label>
                    <select
                        {...register('electricityContract', { required: true })}
                        style={FIELD_STYLE}
                    >
                        <option value="">-- Sélectionner --</option>
                        {ELECTRICITY_CONTRACT_OPTIONS.map((contract) => (
                            <option key={contract} value={contract}>{contract}</option>
                        ))}
                    </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Type de refroidissement *</label>
                    <select
                        {...register('coolingType', { required: true })}
                        style={FIELD_STYLE}
                    >
                        {COOLING_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Section: Paramètres avancés */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Paramètres avancés
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Débit minimal pour une demi-ligne [m³/h]</label>
                    <input type="number" step="0.1" {...register('minFlowHalfLine')} placeholder="50" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Débit minimal pour une ligne complète [m³/h]</label>
                    <input type="number" step="0.1" {...register('minFlowFullLine')} placeholder="100" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Débit maximal [m³/h]</label>
                    <input type="number" step="0.1" {...register('maxFlow')} placeholder="500" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Priorité de la ligne au sein du site de production</label>
                    <input type="number" step="1" {...register('priority')} placeholder="1" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Ligne abaissant en post production</label>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="radio"
                                {...register('postProductionShutdown')}
                                value="true"
                                style={{ cursor: 'pointer', width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 14 }}>Oui</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="radio"
                                {...register('postProductionShutdown')}
                                value="false"
                                defaultChecked
                                style={{ cursor: 'pointer', width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 14 }}>Non</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Section: Équipement présent */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Équipement présent
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Groupes de pompes évaporateurs</label>
                    <input
                        type="text"
                        value={initialData?.evaporatorPumpGroups?.join(', ') || 'Aucun groupe configuré'}
                        readOnly
                        style={{ ...FIELD_STYLE, backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Groupes froid</label>
                    <input
                        type="text"
                        value={initialData?.chillers?.join(', ') || 'Aucun groupe froid configuré'}
                        readOnly
                        style={{ ...FIELD_STYLE, backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Groupes de pompes condenseurs</label>
                    <input
                        type="text"
                        value={initialData?.condenserPumpGroups?.join(', ') || 'Aucun groupe configuré'}
                        readOnly
                        style={{ ...FIELD_STYLE, backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }}
                    />
                </div>
            </div>
        </RightSideModal>
    );
}
